<template>
    <div v-if="!isFetching" class="d-flex flex-column">
        <step-header :title="$t(`${currentStep.name}.title`)" button-id="popover-info" />

        <engage-popover
            :header="$t(`${currentStep.name}.title`)"
            :intro1="$t(`${currentStep.name}.intro1`)"
            placement="bottom"
            target="popover-info"
        />
        <search-bar class="ml-1" @searchValueChanged="searchValueChanged" />

        <b-table
            ref="refTable"
            :busy="isFetching"
            :empty-text="$t('paginator.no-matching-records-found')"
            :fields="tableColumns"
            :items="filteredChannels"
            :no-sort-reset="true"
            :sort-by.sync="sortBy"
            :sort-desc.sync="isSortDirDesc"
            class="mt-2"
            no-select-on-click
            primary-key="id"
            responsive
            select-mode="single"
            selectable
            show-empty
            sticky-header="100%"
            @row-clicked="rowClicked"
        >
            <template #table-busy>
                <div class="d-flex justify-content-center my-1">
                    <b-spinner />
                </div>
            </template>
            <template #cell(checkbox)="data">
                <b-form-radio
                    v-model.number="selected"
                    :value="data.item.id"
                    class="p-0 m-0"
                    name="channel"
                    @change="rowClicked(data.item, data.index)"
                />
            </template>
            <template #cell(name)="data">
                <div class="d-flex align-items-center">
                    <div class="d-flex whatsapp-icon-wrapper rounded-circle mr-1">
                        <p class="m-auto p-0 saysimple-icon whatsapp large text-white"></p>
                    </div>
                    <p class="p-0 m-0">{{ data.item.name }}</p>
                </div>
            </template>
        </b-table>
    </div>
</template>

<script>
import { BFormRadio, BSpinner, BTable } from "bootstrap-vue";
import engagePopover from "../../components/engagePopover.vue";
import stepHeader from "../components/stepHeader.vue";
import searchBar from "../components/searchBar.vue";
import { ref, watch } from "@vue/composition-api";
import useChannels from "@/composables/v2.1/useChannels";
import i18n from "@/libs/i18n";
import useEngageForm from "@/views/modules/engage/useEngageForm";
import { getUserData } from "@/auth/getUserData";

export default {
    components: {
        BTable,
        BSpinner,
        BFormRadio,
        engagePopover,
        stepHeader,
        searchBar,
    },
    data() {
        return {
            searchQuery: "",
        };
    },
    emits: ["channelSelected"],
    computed: {
        filteredChannels() {
            if (getUserData().role === "admin" || getUserData().role === "saysimple") {
                return this.channels.filter((channel) =>
                    channel.name.toLowerCase().includes(this.searchQuery.toLowerCase())
                );
            }

            const channelIds = this.channels
                .filter((channel) =>
                    channel.userChannels?.map((userChannel) => userChannel.userId).includes(getUserData().id)
                )
                .map((channel) => channel.id);

            return this.channels
                .filter((channel) => channel.name.toLowerCase().includes(this.searchQuery.toLowerCase()))
                .filter((channel) => channelIds.includes(channel.id));
        },
    },
    watch: {
        selected(newValue, _) {
            if (newValue !== null) {
                this.$emit("channelSelected", { channel: this.channels.find((channel) => channel.id === newValue) });
            }
        },
    },
    methods: {
        searchValueChanged(value) {
            this.searchQuery = value;
        },
    },
    setup(props, { emit, root }) {
        const { isFetching, channels, fetchChannels } = useChannels();
        const selected = ref(null);
        const refTable = ref(null);
        const sortBy = ref("name");
        const isSortDirDesc = ref(false);

        fetchChannels({
            params: {
                filter: ["platform||$in||WHATSAPP,TEST"],
                join: ["userChannels||userId"],
                sort: `${sortBy.value},${isSortDirDesc.value ? "DESC" : "ASC"}`,
            },
        });

        const rowClicked = (item, index) => {
            selected.value = item.id;
            refTable.value.selectRow(index);
        };

        const tableColumns = [
            { key: "checkbox", label: "", tdClass: "channel-radio-button-column" },
            {
                key: "name",
                label: i18n.t("engage.lists.table-column-channel-name"),
                sortable: true,
            },
        ];

        const { currentStep, selectedEngageToEdit } = useEngageForm(root.$bvModal);
        // in the case when we edit
        if (selectedEngageToEdit.value) {
            currentStep.value = { ...currentStep.value, channel: selectedEngageToEdit.value.channel, isDone: true };
            emit("channelSelected", { channel: selectedEngageToEdit.value.channel });
        }
        if (currentStep.value.channel) {
            selected.value = currentStep.value.channel.id;
        }

        // We need to watch for changes, because any change will undo the selection
        // and /or our selected item might be somewhere else now
        watch([() => refTable?.value?.localItems, isFetching, sortBy, isSortDirDesc], (newValues, oldValues) => {
            if (isFetching.value || !refTable?.value?.localItems.length) {
                return;
            }

            // Bootstrap table doesn't re-sort its internal array. We do this for them so the index we find lines up
            if (newValues[2] !== oldValues[2] || newValues[3] !== oldValues[3]) {
                const newSortBy = newValues[2];
                const sortModifier = newValues[3] ? -1 : 1;
                refTable.value.localItems.sort((channelA, channelB) => {
                    if (typeof channelA[newSortBy] === "number" && typeof channelB[newSortBy] === "number") {
                        return (channelA[newSortBy] - channelB[newSortBy]) * sortModifier;
                    }
                    return `${channelA[newSortBy]}`.localeCompare(`${channelB[newSortBy]}`) * sortModifier;
                });
            }

            const selectedIndex = refTable.value.localItems.findIndex((item) => item.id === selected.value);
            refTable.value.selectRow(selectedIndex);
        });

        return {
            tableColumns,
            channels,
            selected,
            isFetching,
            currentStep,
            refTable,
            sortBy,
            isSortDirDesc,
            rowClicked,
        };
    },
};
</script>
<style lang="scss">
@import "~@/assets/scss/modules/engage/selectChannel.scss";
</style>
